/* =============================================================================
 * Roteador (History API).
 *
 * URLs limpas porque link de jogo e' compartilhado no WhatsApp - hash atrapalha
 * preview e start_url do manifest. O custo e' uma regra de rewrite no servidor
 * estatico (web.config), que ja' esta' no repositorio.
 *
 * Cada view exporta { titulo, montar(ctx), aoAtualizarQuery?, desmontar? }.
 *
 * Detalhe que faz diferenca em 3G: o esqueleto declarado pela rota e' pintado
 * ANTES do import() dinamico resolver. Sem isso o code-splitting parece
 * travamento.
 * ========================================================================== */

import { emitir, EVENTOS } from '@shared/lib/eventos.js';
import { daSessao } from '@shared/lib/armazenamento.js';
import { rotas } from './rotas.js';
import { esqueletoDaRota } from '@shared/ui/componentes/skeleton.js';

let saida = null;
let viewAtual = null;
let controladorAtual = null;
let rotaAtual = null;
let chaveHistorico = null;
//Para onde a proxima pintura rola. Link novo vai para o topo; voltar/avancar
//volta para onde a pessoa estava - ver o bloco de rolagem la' embaixo.
let scrollAlvo = 0;

/**
 * '/competicoes/classificacao/:id' -> regex com grupos nomeados.
 *
 * O sufixo `?` marca parametro OPCIONAL: `/competicoes/rodadas/:id?` casa tanto
 * `/competicoes/rodadas` quanto `/competicoes/rodadas/12`. E' o que permite a
 * competicao sair da URL interna sem quebrar o link ja' compartilhado por fora -
 * ver shared/sessao/competicao-atual.js. So' faz sentido no fim do padrao: um
 * opcional no meio deixaria a posicao dos seguintes ambigua.
 */
function compilar(padrao) {
    if (padrao === '*') return { regex: /^.*$/, nomes: [] };
    const nomes = [];
    const fonte = padrao
        .split('/')
        .map((parte) => {
            if (!parte) return '';
            if (parte.startsWith(':')) {
                const opcional = parte.endsWith('?');
                nomes.push(parte.slice(1, opcional ? -1 : undefined));
                return opcional ? '(?:/([^/]+))?' : '/([^/]+)';
            }
            return '/' + parte.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        })
        .join('');
    return { regex: new RegExp(`^${fonte || '/'}/?$`), nomes };
}

const compiladas = rotas.map((r) => ({ ...r, ...compilar(r.padrao) }));

/** Exportada para teste: e' a regra que decide qual pagina atende uma URL. */
export function casar(caminho) {
    for (const rota of compiladas) {
        const m = rota.regex.exec(caminho);
        if (!m) continue;
        const params = {};
        //Parametro opcional ausente vira undefined, nao a string 'undefined'.
        rota.nomes.forEach((nome, i) => (params[nome] = m[i + 1] === undefined ? undefined : decodeURIComponent(m[i + 1])));
        return { rota, params };
    }
    return null;
}

function contextoAtual(rota, params, sinal) {
    const url = new URL(location.href);
    return {
        params,
        query: Object.fromEntries(url.searchParams),
        url: url.pathname + url.search,
        caminho: url.pathname,
        sinal,
        raiz: saida,
        navegar,
        definirQuery,
        rota,
    };
}

export function navegar(destino, { substituir = false } = {}) {
    if (destino === location.pathname + location.search) return;
    guardarScroll();
    if (substituir) {
        history.replaceState(estadoDaEntrada(), '', destino);
    } else {
        chaveHistorico = novaChave();
        //EMPILHAR DESCARTA O QUE ESTAVA A' FRENTE. E' a regra do navegador, e sem
        //repeti-la aqui o botao de avancar continuaria aceso apontando para
        //entradas que o proprio pushState acabou de destruir.
        posicao += 1;
        limite = posicao;
        history.pushState(estadoDaEntrada(), '', destino);
    }
    anotarHistorico();
    //PAGINA NOVA COMECA NO TOPO. Nao ha' o que restaurar aqui: ninguem esteve
    //nesta tela ainda, e quem clicou no link estava no meio da anterior.
    scrollAlvo = 0;
    resolver();
}

/**
 * Troca so' a query string. Se a view souber reagir (aoAtualizarQuery), nao
 * remonta: e' o que mantem o scroll ao trocar de categoria.
 *
 * A VIEW E' AVISADA MESMO QUANDO A URL NAO MUDA. Parece contradicao, mas nem
 * todo filtro mora inteiro na query: a categoria tambem fica salva no aparelho
 * (ver ui/componentes/filtro-categoria.js), e "todas as categorias" e' a
 * AUSENCIA de `?cat=`. Quem estava numa categoria vinda do armazenamento, com a
 * URL limpa, e escolhia "todas" caia num `definirQuery({ cat: '' })` que apagava
 * um parametro que nunca esteve la': a URL ficava identica, a funcao saia pelo
 * atalho, e a tela continuava mostrando a categoria antiga ate' alguem recarregar
 * a pagina. A escolha ja' tinha sido gravada - o que faltava era repintar.
 *
 * O atalho continua valendo para o HISTORICO (nao ha' o que empilhar quando o
 * endereco e' o mesmo) e para o REMONTE do ramo de baixo, que troca o DOM
 * inteiro e nao deve rodar a' toa.
 */
export function definirQuery(alteracoes, { substituir = true } = {}) {
    const url = new URL(location.href);
    for (const [chave, valor] of Object.entries(alteracoes)) {
        if (valor === null || valor === undefined || valor === '') url.searchParams.delete(chave);
        else url.searchParams.set(chave, String(valor));
    }
    const destino = url.pathname + url.search;
    const mudouUrl = destino !== location.pathname + location.search;

    if (mudouUrl) {
        if (substituir) {
            history.replaceState(estadoDaEntrada(), '', destino);
        } else {
            chaveHistorico = novaChave();
            posicao += 1;
            limite = posicao;
            history.pushState(estadoDaEntrada(), '', destino);
        }
        anotarHistorico();
    }

    if (viewAtual?.aoAtualizarQuery) {
        viewAtual.aoAtualizarQuery(contextoAtual(rotaAtual, ultimosParams, controladorAtual?.signal));
    } else if (mudouUrl) {
        //Remontou a tela inteira por causa de um filtro: e' uma lista nova, e ela
        //comeca de cima. Quem sabe manter o lugar e' `aoAtualizarQuery`, no ramo
        //de cima - la' o DOM nem chega a ser trocado.
        scrollAlvo = 0;
        resolver();
    }
}

let ultimosParams = {};

/* ---- rolagem ------------------------------------------------------------
 *
 * DUAS REGRAS, e nao uma so': link novo comeca no topo, voltar/avancar volta
 * para onde a pessoa estava. Sem separar as duas, todo destino dependia de
 * acertar a chave da entrada - e quando ela errava, a tela nova abria no meio.
 *
 * A CHAVE DA ENTRADA NAO PODE SER UM CONTADOR. Era: 0, 1, 2... reiniciado a cada
 * carga do app, enquanto o mapa de posicoes vive no sessionStorage e sobrevive ao
 * F5. Depois de recarregar, a primeira navegacao virava a chave 1 de novo e
 * herdava a rolagem que a chave 1 da rodada anterior tinha guardado: a pagina
 * nova abria no meio, sem nada na tela explicando por que. Chave unica por
 * entrada acaba com a colisao - e como ela e' preservada no F5 (ver `iniciar`),
 * voltar depois de recarregar continua achando a posicao certa.
 */
const novaChave = () => `${Date.now().toString(36)}.${Math.random().toString(36).slice(2, 8)}`;

//O mapa e' de uma aba so', mas nada o apaga enquanto ela viver. O teto evita que
//uma sessao longa carregue centenas de posicoes que ninguem vai mais visitar -
//so' as ultimas importam, porque o historico do navegador tambem tem fim.
const MAXIMO_POSICOES = 50;

function guardarScroll() {
    const mapa = daSessao.ler('scroll', {});
    //So' poda ao ABRIR uma chave nova: reescrever uma que ja' esta' la' nao faz
    //o mapa crescer, e nao pode custar a posicao mais antiga.
    const chaves = Object.keys(mapa);
    if (!(chaveHistorico in mapa) && chaves.length >= MAXIMO_POSICOES) delete mapa[chaves[0]];
    mapa[chaveHistorico] = window.scrollY;
    daSessao.gravar('scroll', mapa);
}

function scrollGuardado() {
    const mapa = daSessao.ler('scroll', {});
    return mapa[chaveHistorico] ?? 0;
}

/**
 * `scrollTo` duas vezes, e nao uma:
 *
 * - assim que o esqueleto entra, para o clique responder na hora - esperar o
 *   `montar()` deixaria a tela nova aparecendo no meio ate' a rede responder;
 * - depois do conteudo, porque so' ai' a pagina tem altura. E' o que faz o
 *   voltar chegar a 800px numa tela que, no esqueleto, tinha 400.
 */
function aplicarScroll() {
    window.scrollTo(0, scrollAlvo);
}

/* ---- voltar / avancar ---------------------------------------------------
 *
 * A History API nao conta: `history.length` inclui o que veio ANTES do app na
 * mesma aba, e nao existe nenhuma pergunta do tipo "da' para avancar?". Entao a
 * pilha e' contada aqui.
 *
 * `posicao` viaja DENTRO do estado da entrada - e' o que faz o numero
 * sobreviver ao voltar, ao avancar e ao F5, sem depender de nenhum contador em
 * memoria. `limite` e' a posicao mais alta ainda alcancavel: empilhar uma
 * entrada nova apaga o que estava a' frente, e ele desce junto.
 *
 * SO' CONTAMOS O QUE E' NOSSO. Quem chega de um link do WhatsApp abre na
 * posicao 0 e o botao de voltar nasce apagado: ele e' a navegacao DO APP, e
 * mandar a pessoa para fora do Futigol nao e' o que um botao dentro dele
 * promete - para sair da aba ja' existe o botao do navegador.
 */
let posicao = 0;
let limite = 0;

const estadoDaEntrada = () => ({ chave: chaveHistorico, posicao });

/**
 * `limite` fica no sessionStorage porque o F5 nao apaga as entradas a' frente:
 * quem recarregou no meio do historico continua podendo avancar, e o numero
 * precisa atravessar a carga junto com o mapa de rolagem.
 */
function anotarHistorico() {
    if (limite < posicao) limite = posicao;
    daSessao.gravar('historico', { limite });
    emitir(EVENTOS.HISTORICO_ALTERADO, { podeVoltar: podeVoltar(), podeAvancar: podeAvancar() });
}

export function podeVoltar() {
    return posicao > 0;
}

export function podeAvancar() {
    return posicao < limite;
}

//`history.back()`/`forward()` e nao `navegar()`: o destino e' a ENTRADA
//anterior, com a rolagem e a chave dela - reconstruir a URL na mao empilharia
//uma entrada nova e o "avancar" nunca teria para onde ir. O popstate faz o
//resto.
export function voltar() {
    if (podeVoltar()) history.back();
}

export function avancar() {
    if (podeAvancar()) history.forward();
}

async function resolver() {
    const caminho = location.pathname;
    const casado = casar(caminho);
    if (!casado) return;

    const { rota, params } = casado;
    ultimosParams = params;

    //Guarda: pode devolver true ou { redirecionar }.
    if (rota.guarda) {
        const contexto = contextoAtual(rota, params, null);
        const resultado = rota.guarda(contexto);
        if (resultado !== true) {
            navegar(resultado?.redirecionar ?? '/', { substituir: true });
            return;
        }
    }

    //Encerra a rota anterior antes de qualquer coisa: aborta as requisicoes em
    //voo e deixa a view limpar timers e listeners.
    controladorAtual?.abort();
    try {
        viewAtual?.desmontar?.();
    } catch (e) {
        console.error('[roteador] falha ao desmontar', e);
    }

    controladorAtual = new AbortController();
    rotaAtual = rota;

    //Esqueleto imediato, antes de esperar o chunk da rota.
    saida.replaceChildren(esqueletoDaRota(rota.esqueleto));
    aplicarScroll();

    let modulo;
    try {
        modulo = await rota.carregar();
    } catch (e) {
        console.error('[roteador] falha ao carregar a rota', e);
        const { estadoErro } = await import('@shared/ui/componentes/estado-erro.js');
        saida.replaceChildren(estadoErro(e, { aoTentarDeNovo: () => resolver() }));
        return;
    }

    viewAtual = modulo.default ?? modulo;
    const contexto = contextoAtual(rota, params, controladorAtual.signal);

    document.title = typeof viewAtual.titulo === 'function' ? `${viewAtual.titulo(contexto)} · Futigol` : 'Futigol';

    emitir(EVENTOS.ROTA_ALTERADA, { rota, params, caminho });

    try {
        await viewAtual.montar(contexto);
        //Depois do paint, senao a pagina ainda nao tem altura para rolar.
        requestAnimationFrame(aplicarScroll);
    } catch (e) {
        if (e.name === 'AbortError') return; //navegou para fora, nao e' erro
        console.error('[roteador] falha ao montar a view', e);
        const { estadoErro } = await import('@shared/ui/componentes/estado-erro.js');
        saida.replaceChildren(estadoErro(e, { aoTentarDeNovo: () => resolver() }));
    }
}

export function iniciar(containerSaida) {
    saida = containerSaida;
    //O browser restaura scroll sozinho e briga com o nosso; desligamos.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    //RECARREGAR TAMBEM E' SAIR DA ENTRADA. Com `scrollRestoration: manual` quem
    //devolve a posicao depois do F5 e' o app, e sem guardar aqui ele devolveria a
    //de quando a pessoa saiu desta entrada da ultima vez - nao a de agora.
    window.addEventListener('pagehide', guardarScroll);

    window.addEventListener('popstate', () => {
        //A entrada que estamos DEIXANDO ainda e' a `chaveHistorico` de agora, e a
        //rolagem dela ainda esta' na tela: o DOM so' troca depois daqui. E' o que
        //faz o "avancar" achar o lugar de onde o "voltar" saiu.
        guardarScroll();
        chaveHistorico = history.state?.chave ?? novaChave();
        posicao = history.state?.posicao ?? 0;
        anotarHistorico();
        scrollAlvo = scrollGuardado();
        resolver();
    });

    //A CHAVE DA ENTRADA SOBREVIVE AO F5: quem recarrega no meio do historico
    //continua sendo a mesma entrada, e voltar dali ainda acha as posicoes
    //guardadas. Chave nova a cada carga era o que fazia as chaves colidirem.
    chaveHistorico = history.state?.chave ?? novaChave();

    //ENTRADA NOSSA x ENTRADA NOVA. Recarregar preserva o estado, e com ele a
    //posicao e o direito de avancar. Sair do Futigol e voltar pelo endereco
    //abre uma entrada SEM estado: ali o `limite` guardado na aba e' de uma
    //pilha que nao existe mais, e herda-lo deixaria um "avancar" aceso que nao
    //leva a lugar nenhum.
    const nossa = typeof history.state?.posicao === 'number';
    posicao = nossa ? history.state.posicao : 0;
    limite = nossa ? Math.max(posicao, daSessao.ler('historico', {})?.limite ?? 0) : 0;

    history.replaceState(estadoDaEntrada(), '', location.pathname + location.search);
    anotarHistorico();
    scrollAlvo = scrollGuardado();
    resolver();
}

export function rotaEmUso() {
    return rotaAtual;
}
